import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useMap, Marker } from 'react-leaflet';
import L from 'leaflet';
import { Navigation, Crosshair } from 'lucide-react';
import { toast } from 'sonner';

// CSS de animación inyectado una sola vez en document.head
const GEO_CSS = `
.geo-pos-icon { background: none !important; border: none !important; }
.geo-pos-dot {
  width: 14px; height: 14px;
  background: #3B82F6;
  border: 2.5px solid white;
  border-radius: 50%;
  position: relative;
  box-shadow: 0 1px 4px rgba(0,0,0,0.25);
}
.geo-pos-dot.lost { background: #9CA3AF; }
.geo-pos-ring {
  position: absolute;
  top: 50%; left: 50%;
  width: 30px; height: 30px;
  border: 2px solid rgba(59,130,246,0.55);
  border-radius: 50%;
  pointer-events: none;
  animation: geo-pulse 1.8s ease-out infinite;
}
@keyframes geo-pulse {
  0%   { transform: translate(-50%,-50%) scale(0.25); opacity: 1; }
  100% { transform: translate(-50%,-50%) scale(1.7);  opacity: 0; }
}
`;

export default function MapaLocateButton({ onCercaDeMi }) {
  const map = useMap();
  const watchRef = useRef(null);
  const [pos, setPos] = useState(null);      // { lat, lng }
  const [posLost, setPosLost] = useState(false);

  // Inyectar CSS una sola vez
  useEffect(() => {
    if (!document.getElementById('geo-pulse-css')) {
      const s = document.createElement('style');
      s.id = 'geo-pulse-css';
      s.textContent = GEO_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // watchPosition — activo mientras el usuario esté en el mapa
  useEffect(() => {
    if (!navigator.geolocation) return;
    watchRef.current = navigator.geolocation.watchPosition(
      ({ coords }) => {
        setPos({ lat: coords.latitude, lng: coords.longitude });
        setPosLost(false);
      },
      (err) => {
        if (err.code !== 3) setPosLost(true); // code 3 = timeout, ignorar
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => {
      if (watchRef.current != null) {
        navigator.geolocation.clearWatch(watchRef.current);
      }
    };
  }, []);

  // DivIcon con animación de pulso
  const posIcon = useMemo(() => {
    if (!pos) return null;
    const ring = posLost ? '' : '<div class="geo-pos-ring"></div>';
    return L.divIcon({
      className: 'geo-pos-icon',
      html: `<div class="geo-pos-dot${posLost ? ' lost' : ''}">${ring}</div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
  }, [pos, posLost]);

  const handleLocate = () => {
    if (pos) {
      map.setView([pos.lat, pos.lng], 13);
    } else {
      toast.info('Obteniendo ubicación…');
    }
  };

  const handleCercaDeMi = () => {
    if (pos) {
      map.setView([pos.lat, pos.lng], 11);
      if (onCercaDeMi) onCercaDeMi(pos.lat, pos.lng);
    } else {
      toast.error('Ubicación no disponible aún');
    }
  };

  return (
    <>
      {/* Marcador de posición actual */}
      {pos && posIcon && (
        <Marker position={[pos.lat, pos.lng]} icon={posIcon} interactive={false} />
      )}

      {/* Controles */}
      <div className="leaflet-bottom leaflet-left" style={{ marginBottom: 30, marginLeft: 10, zIndex: 1000 }}>
        <div className="leaflet-control leaflet-bar">
          <a href="#locate" title="Centrar en mi posición" role="button"
            onClick={(e) => { e.preventDefault(); handleLocate(); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30 }}
          >
            <Navigation size={14} style={{ color: pos && !posLost ? '#3B82F6' : undefined }} />
          </a>
          <a href="#cerca" title="Entidades cerca de mí (25 km)" role="button"
            onClick={(e) => { e.preventDefault(); handleCercaDeMi(); }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30 }}
          >
            <Crosshair size={14} />
          </a>
        </div>
      </div>
    </>
  );
}
